<template>
  <div class="header-strip rows align-items-center justify-space-between">
    <img class="logo" src="@/assets/images/logo.png" :alt="$t('title')">
    <div class="input-icon search desktop">
      <i class="bi bi-search search-icon"></i>
      <input type="text" class="borderless" @input="onSearch" v-model="searchToken" ref="search">
      <button v-if="searchToken.length > 0"
              @click="clearSearch"
              class="transparent clear-icon">
        <i class="bi bi-x-circle-fill"></i>
      </button>
    </div>
    <CurrentTime :date-time-moment="currentTimeInMoment" class="desktop"/>
    <select
        class="language-menu borderless desktop"
        :value="$i18n.locale"
        @change="changeLocale($event.target.value)"
    >
      <option value="en">🇬🇧 English</option>
      <option value="bg">🇧🇬 Български</option>
      <option value="es">🇪🇸 Español</option>
      <option value="gr">GR Greek</option>
      <option value="de">DE Deutsch</option>
      <option value="ro">RO Romanian</option>
    </select>
    <button class="transparent notification-icon-size" type="button" @click="toggleEventsList()" v-if="!guestMode">
      <i class="bi bi-bell large-icon">
        <span class="notification-icon-badge" v-if="hasPendingNotifications"></span>
      </i>
    </button>
    <button v-if="violations !== null && hasViolations"
            @click="showViolations"
            class="button-save">
      {{ $t('header.show-violations') }}
    </button>
    <button v-if="isDashboardOpen" class="button-save" @click="changeView">
      {{ $t(newView ? 'header.old-view' : 'header.new-view') }}
    </button>
    <div class="header-avatar rows justify-space-between align-items-center desktop" v-if="!guestMode">
      <div class="header-avatar-image">
        <img src="@/assets/images/navbar/avatar.png" :alt="userName">
      </div>
      <div class="header-avatar-title cols align-items-start">
        <span class="header-avatar-title-name">
          {{ userName }}
        </span>
        <span class="header-avatar-title-greeting">
          {{ greeting }}
        </span>
      </div>
    </div>

    <button type="button" class="mobile-menu transparent mobile" @click="toggleNavbar" v-if="!guestMode">
      <i class="bi bi-list"></i>
    </button>
  </div>
</template>

<script>
import moment from 'moment';
import {mapGetters, mapState} from 'vuex';
import CurrentTime from '@/components/pages/partials/CurrentTime';
import {mutations} from "@/assets/js/vue/vuex/actions";
import {entityDataManager} from "@/assets/js/data-manipulation";
import {popups} from "@/assets/js/popups";
import paths from "@/assets/js/vue/router/paths";

export default {
  name: 'Header',
  components: {
    CurrentTime,
  },
  props: {
    guestMode: {type: Boolean, default: false},
  },
  data() {
    return {
      currentTimeInMoment: moment(),
      searchToken: '',
    };
  },
  computed: {
    ...mapState(['session', 'events', 'violations', 'vehicles', 'newView']),
    ...mapGetters(['hasViolations', 'userCompany']),
    userName: function() {
      return this.session.name || this.$t('header.anonymous');
    },
    greeting() {
      return parseInt(this.currentTimeInMoment.format('HH')) > 12 ?
          this.$t('generic.good-afternoon') :
          this.$t('generic.good-morning');
    },
    hasPendingNotifications() {
      return entityDataManager.hasNonSeenEvent(this.events);
    },
    isDashboardOpen() {
      return this.$route.path === paths.home;
    },
  },
  mounted() {
    setInterval(() => this.currentTimeInMoment = moment(), 60000);
  },
  methods: {
    changeLocale(newLocale) {
      this.$store.commit(mutations.SET_LOCALE, newLocale);
    },
    toggleNavbar() {
      this.$emit('toggle-navbar');
    },
    onSearch() {
      this.$emit('on-search', this.searchToken);
    },
    clearSearch() {
      this.searchToken = '';
      this.onSearch();
      this.$refs.search.focus();
    },
    toggleEventsList() {
      this.$store.commit(mutations.TOGGLE_EVENTS_PANEL, true);
    },
    showViolations() {
      popups.showViolations(this.$swal, this.violations, this.vehicles, this.userCompany);
    },
    changeView() {
      this.$store.commit(mutations.SET_USE_NEW_DASHBOARD_VIEW, !this.newView);
    }
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables.scss';

.header-strip {
  width: calc(100% - 40px);
  height: $header-strip-height;
  padding: 0 20px;

  .large-icon {
    font-size: 30px;
  }

  background: #F4F6FA;
  color: $default-text-color;
  font-size: 1.2rem;

  .logo {
    height: 50%;
  }

  .notification-icon-size {
    width: 50px;
    height: 50px;

    .notification-icon-badge {
      position: absolute;
      font-size: xx-small;
      margin-left: -5px;
      margin-top: -5px;
      background-color: red;
      width: 10px;
      height: 10px;
      border-radius: 50%;
    }
  }

  .search {
    width: 16%;
  }

  .language-menu {
    font-size: 1.2rem;
  }

  .header-avatar {
    height: 60%;

    .header-avatar-image {
      height: 100%;

      & > img {
        height: 100%;
      }
    }

    .header-avatar-title {
      margin-left: 12px;
      color: #07255B;

      .header-avatar-title-name {
        font-weight: 500;
        font-size: 1.2rem;
        line-height: 27px;
      }

      .header-avatar-title-greeting {
        font-weight: 400;
        font-size: 0.75rem;
        line-height: 18px;
      }
    }
  }

  .mobile-menu {
    font-size: 2.5rem;
    color: #414351;
  }
}

// Mobile:
@media (max-width: $breaking-point) {
  .header-strip {
    position: sticky;
    top: 0;
    z-index: 11;
  }
}

// Phone:
@media (max-width: $breaking-point-phone) {
  .header-strip {
    .logo {
      height: 35% !important;
    }
  }
}
</style>
